import { Injectable } from "@angular/core";

import { first } from "rxjs/operators";

import { DffCurrentRoute, DffRedirectService } from "@dffedb/router";

import { UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { isTeknikerContext } from "../util/tekniker-context";
import { getLoginRouteByPreference } from "../util/login-route.util";

@Injectable({ providedIn: "root" })
export class RedirectService {
    constructor(
        private readonly redirectService: DffRedirectService,
        private readonly currentRoute: DffCurrentRoute,
        private readonly indstillinger: UmbracoIndstillingerObserver
    ) {}

    public redirectTilDefault(): void {
        this.redirectService.redirect("/");
    }

    public redirectTilLogin(redirectTilAktuelleRouteEfterLogin: boolean = false): void {
        const redirectUrl = redirectTilAktuelleRouteEfterLogin ? this.currentRoute.value.path : "";
        let lastLoginPage: string | null = null;
        try {
            lastLoginPage = localStorage.getItem("sidste-login-side");
        } catch {}
        const loginRoute = getLoginRouteByPreference(lastLoginPage, isTeknikerContext());
        this.redirectService.redirect(`${loginRoute}${redirectUrl}`);
    }

    public redirectTilFlyttekvittering(parameter: any): void {
        this.redirectService.redirect("/meld-flytning/kvittering", { data: btoa(JSON.stringify(parameter)) });
    }

    public redirectTilInstallationer(): void {
        this.redirectService.redirect("/install-picker");
    }

    public redirectTilMitForbrug(): void {
        this.redirectService.redirect("/mit-forbrug");
    }

    public redirectTilMineDokumenter(): void {
        this.redirectService.redirect("/mine-dokumenter");
    }

    public redirect(path: string, params?: any): void {
        this.redirectService.redirect(path, params);
    }

    private removeStartUrlFromIndexedDb(): Promise<void> {
        return new Promise((resolve) => {
            if (typeof indexedDB === "undefined") return resolve();

            const DB_NAME = "vaerkslisteDB";
            const SETTINGS_STORE = "settings";
            let req: IDBOpenDBRequest;

            try {
                req = indexedDB.open(DB_NAME); // no version -> no forced upgrade
            } catch {
                return resolve();
            }

            req.onerror = () => resolve();

            req.onsuccess = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
                    db.close();
                    return resolve();
                }
                try {
                    const tx = db.transaction(SETTINGS_STORE, "readwrite");
                    const store = tx.objectStore(SETTINGS_STORE);
                    store.delete("startUrl");
                    tx.oncomplete = () => {
                        db.close();
                        resolve();
                    };
                    tx.onerror = () => {
                        db.close();
                        resolve();
                    };
                    tx.onabort = () => {
                        db.close();
                        resolve();
                    };
                } catch {
                    db.close();
                    resolve();
                }
            };
        });
    }

    public redirectTilVaerksListe(): void {
        this.indstillinger.valueChanges
            .pipe(first())
            .subscribe((indstillinger) => {
                const url = this.buildVaerkslisteUrl(indstillinger.vaerkslisteUrl);
                location.href = url;
            });
    }

    private buildVaerkslisteUrl(vaerkslisteUrl: string): string {
        if (typeof window === "undefined") {
            const separator = vaerkslisteUrl.includes("?") ? "&" : "?";
            return `${vaerkslisteUrl}${separator}clear=true`;
        }

        try {
            const parsedUrl = new URL(vaerkslisteUrl, window.location.origin);
            if (this.isDevTeknikHost(window.location.hostname)) {
                parsedUrl.protocol = window.location.protocol;
                parsedUrl.host = window.location.host;
            }

            parsedUrl.searchParams.set("clear", "true");
            return parsedUrl.toString();
        } catch {
            const separator = vaerkslisteUrl.includes("?") ? "&" : "?";
            return `${vaerkslisteUrl}${separator}clear=true`;
        }
    }

    private isDevTeknikHost(hostname: string): boolean {
        const normalizedHostname = (hostname || "").toLowerCase();
        return (
            normalizedHostname.startsWith("dev-teknik.") ||
            normalizedHostname.startsWith("servicepro.") ||
            normalizedHostname.startsWith("dev-servicepro.") ||
            normalizedHostname.startsWith("dev-servicepro-")
        );
    }
}
